import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TopNavBar from '../components/common/TopNavBar';
import { playWord } from '../components/topic/_audio';
import { VOCAB_GAMES_CATALOG } from './TopicGamePlayPage';

// ─────────────────────────────────────────────────────────────────────────────
// SỔ TỪ hợp nhất (Thư viện + Sổ từ cũ):
//   /vocab/words?tab=vocab|grammar&level=all|starters|…&topic=<id>&status=…&q=…
// Tab Từ vựng: pills cấp độ → chips chủ đề → tabs trạng thái + tìm kiếm →
// danh sách từ (audio, nghĩa, tier, chọn để luyện). Chọn chủ đề hiện header
// chủ đề với "Học chủ đề này" + modal chọn game. Tab Ngữ pháp: grid chủ đề
// ngữ pháp + danh sách quy tắc inline.
// ─────────────────────────────────────────────────────────────────────────────

const HT = {
  yellow: '#FFD166', yellowSoft: '#FFE9A8', yellowBg: '#FFF8E7', yellowShadow: '#E0B554',
  green: '#88C057', greenDark: '#5E9E3E', greenSoft: '#D4E8C0', greenBg: '#E8F3D9',
  coral: '#FF8C42', coralDark: '#E76F51', coralPress: '#B8431E',
  pinkBg: '#FCE4E8', pink: '#F4A6B8',
  brownDark: '#6B4F2B',
  text: '#2D2D2D', textSoft: '#6B6B6B', textMuted: '#9A9A9A',
  border: '#E8E0CC', white: '#FFFFFF', teal: '#5AC0B5',
};
const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
const FONT_BODY = '"Nunito", "Baloo 2", sans-serif';
const SHADOW_SM = '0 2px 6px rgba(139,111,71,0.08)';
const SHADOW_MD = '0 4px 16px rgba(139,111,71,0.10)';

const MEDIA_BASE = process.env.REACT_APP_MEDIA_URL || 'https://api-staging.hupath.com/uploads';
const CATEGORY_IMG_BASE = `${MEDIA_BASE}/media/images/category/`;

const STATUS_INFO = {
  review_due:        { emoji: '🔴', label: 'Cần ôn lại', color: '#EF4444', bg: '#FEE2E2' },
  needs_improvement: { emoji: '🟠', label: 'Cần luyện thêm', color: '#F59E0B', bg: '#FEF3C7' },
  in_progress:       { emoji: '🟡', label: 'Đang học', color: '#CA8A04', bg: '#FEF9C3' },
  unexplored:        { emoji: '⚪', label: 'Chưa học', color: '#94A3B8', bg: '#F1F5F9' },
  mastered:          { emoji: '🟢', label: 'Đã thuộc', color: '#10B981', bg: '#D1FAE5' },
};

const LEVELS = [
  { key: 'all',      label: 'Tất cả', icon: '🌟' },
  { key: 'starters', label: 'Starters', icon: '🌱' },
  { key: 'movers',   label: 'Movers', icon: '🚀' },
  { key: 'flyers',   label: 'Flyers', icon: '🦋' },
  { key: 'ket',      label: 'KET', icon: '🎓' },
];

const STATUS_TABS = [
  { key: 'all', label: 'Tất cả', emoji: '📚', statuses: null },
  { key: 'learning', label: 'Đang học', emoji: '🟡', statuses: ['in_progress', 'needs_improvement', 'review_due'] },
  { key: 'mastered', label: 'Đã thuộc', emoji: '🟢', statuses: ['mastered'] },
  { key: 'unexplored', label: 'Chưa học', emoji: '⚪', statuses: ['unexplored'] },
];

const MAX_SELECT = 20;

const pill = (active, activeBg = HT.teal) => ({
  padding: '8px 14px', borderRadius: 999,
  border: `1px solid ${active ? activeBg : HT.border}`,
  background: active ? activeBg : HT.white,
  color: active ? HT.white : HT.textSoft,
  fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700,
  boxShadow: active ? '0 2px 0 rgba(0,0,0,0.08)' : SHADOW_SM,
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
  whiteSpace: 'nowrap', flexShrink: 0,
});

export default function VocabWordBookPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'grammar' ? 'grammar' : 'vocab';
  const levelsSel = (params.get('level') || '').toLowerCase().split(',').filter(x => x && x !== 'all');
  const topicId = params.get('topic') || '';
  const statusSel = (params.get('status') || '').split(',').filter(x => x && x !== 'all');
  const q = params.get('q') || '';
  const [sheet, setSheet] = useState(null); // 'level' | 'topic' | 'status' | null
  const [topicQuery, setTopicQuery] = useState('');

  const [words, setWords] = useState(null);
  const [topics, setTopics] = useState(null);       // {vocabulary_topics, grammar_topics}
  const [topicStats, setTopicStats] = useState(null); // /topics/parents/{topicId}
  const [grammarOpen, setGrammarOpen] = useState(null); // grammar topic id expanded
  const [grammarLeaves, setGrammarLeaves] = useState({}); // id -> leaves
  const [grammarLeaf, setGrammarLeaf] = useState(null); // grammar rule (leaf) id whose detail is open
  const [selected, setSelected] = useState(() => new Set());
  const [gameModal, setGameModal] = useState(false);
  const [statusWord, setStatusWord] = useState(null); // word row whose status is being changed
  const [error, setError] = useState(null);
  const stopAudioRef = useRef(null);

  const API = process.env.REACT_APP_API_URL;
  const authHeader = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` }), []);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || value === 'all') next.delete(key); else next.set(key, value);
    if (key === 'level') next.delete('topic'); // topic list depends on level
    setParams(next, { replace: true });
  };

  // ── Words: fetch ALL levels once; level filtering is client-side so the
  //     level dropdown can multi-select without refetching. ──
  useEffect(() => {
    if (tab !== 'vocab' || words) return;
    let alive = true;
    fetch(`${API}/client/vocab/level-words?level=all`, { headers: authHeader() })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => { if (alive) setWords(d.words || []); })
      .catch(e => { if (alive) setError(String(e)); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, tab, authHeader]);

  // ── Topics (chips + grammar grid) ──
  useEffect(() => {
    let alive = true;
    fetch(`${API}/client/library/topics`, { headers: authHeader() })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d) setTopics(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [API, authHeader]);

  // ── Selected topic stats (header card) ──
  useEffect(() => {
    if (!topicId) { setTopicStats(null); return; }
    let alive = true;
    fetch(`${API}/client/topics/parents/${topicId}`, { headers: authHeader() })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive) setTopicStats(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [API, topicId, authHeader]);

  useEffect(() => () => { if (stopAudioRef.current) stopAudioRef.current(); }, []);
  useEffect(() => { setSelected(new Set()); }, [levelsSel.join(','), topicId, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const vocabTopics = topics?.vocabulary_topics || [];
  const grammarTopics = topics?.grammar_topics || [];
  const activeTopic = vocabTopics.find(t => t.tag_id === topicId)
    || (topicStats ? { tag_id: topicId, name: topicStats.name, code: topicStats.tag_code } : null);

  // ── Filtering ──
  const topicFiltered = useMemo(() => {
    if (!words) return null;
    let list = words;
    if (levelsSel.length) list = list.filter(w => levelsSel.includes((w.level || '').toLowerCase()));
    if (topicId) list = list.filter(w => w.topic_id === topicId);
    return list;
  }, [words, topicId, levelsSel.join(',')]);

  const counts = useMemo(() => {
    const c = { all: 0, learning: 0, mastered: 0, unexplored: 0 };
    (topicFiltered || []).forEach(w => {
      c.all += 1;
      if (w.status === 'mastered') c.mastered += 1;
      else if (w.status === 'unexplored') c.unexplored += 1;
      else c.learning += 1;
    });
    return c;
  }, [topicFiltered]);

  const visible = useMemo(() => {
    if (!topicFiltered) return [];
    const allowed = new Set();
    statusSel.forEach(k => (STATUS_TABS.find(x => x.key === k)?.statuses || []).forEach(st => allowed.add(st)));
    const qq = q.trim().toLowerCase();
    let list = topicFiltered;
    if (allowed.size) list = list.filter(w => allowed.has(w.status));
    if (qq) list = list.filter(w =>
      (w.name || '').toLowerCase().includes(qq) || (w.name_vi || '').toLowerCase().includes(qq));
    return [...list.filter(w => w.review_due), ...list.filter(w => !w.review_due)];
  }, [topicFiltered, statusSel.join(','), q]);

  const toggleSelect = (tagId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId);
      else if (next.size < MAX_SELECT) next.add(tagId);
      return next;
    });
  };

  const practice = (tagIds) => {
    if (!tagIds.length) return;
    const lv = levelsSel.length === 1 ? levelsSel[0] : 'all';
    navigate(`/vocab/${lv}/play?tags=${tagIds.slice(0, MAX_SELECT).join(',')}`);
  };

  const tabPracticePool = useMemo(() => {
    const due = visible.filter(w => w.review_due);
    const rest = visible.filter(w => !w.review_due);
    for (let i = rest.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [rest[i], rest[j]] = [rest[j], rest[i]];
    }
    return [...due, ...rest].slice(0, MAX_SELECT).map(w => w.tag_id);
  }, [visible]);

  // Pool of NOT-yet-mastered words (review-due first, then random), for the
  // "start practising unlearned words" button shown when nothing is selected.
  const unlearnedPool = useMemo(() => {
    const pool = visible.filter(w => w.status !== 'mastered');
    const due = pool.filter(w => w.review_due);
    const rest = pool.filter(w => !w.review_due);
    for (let i = rest.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [rest[i], rest[j]] = [rest[j], rest[i]];
    }
    return [...due, ...rest].slice(0, MAX_SELECT).map(w => w.tag_id);
  }, [visible]);

  const speak = (w) => {
    if (stopAudioRef.current) stopAudioRef.current();
    stopAudioRef.current = playWord(null, w.name);
  };
  const tierStars = (tier) => '⭐'.repeat(Math.max(0, Math.min(5, tier))) || '·';


  // Manually flip a word's status (per-row action). Optimistic update.
  const changeWordStatus = (w, newKey) => {
    const mapped = { mastered: 'mastered', in_progress: 'in_progress', unexplored: 'unexplored' }[newKey];
    if (!mapped) return;
    setStatusWord(null);
    setWords(prev => prev && prev.map(x => {
      if (x.tag_id !== w.tag_id && !(mapped === 'mastered' && (x.name || '').toLowerCase() === (w.name || '').toLowerCase() && x.level !== w.level && x.status !== 'mastered')) return x;
      return {
        ...x,
        status: mapped,
        current_tier: mapped === 'mastered' ? 5 : mapped === 'unexplored' ? 0 : Math.max(1, x.current_tier || 1),
        review_due: false,
      };
    }));
    fetch(`${API}/client/vocab/word-status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({ tag_id: w.tag_id, status: mapped }),
    }).catch(() => {});
  };

  // ── Grammar leaves loader ──
  const openGrammar = (gid) => {
    const next = grammarOpen === gid ? null : gid;
    setGrammarOpen(next);
    if (next && !grammarLeaves[next]) {
      fetch(`${API}/client/topics/parents/${next}`, { headers: authHeader() })
        .then(r => (r.ok ? r.json() : null))
        .then(d => { if (d) setGrammarLeaves(prev => ({ ...prev, [next]: d.leaves || [] })); })
        .catch(() => {});
    }
  };

  const masteryPct = topicStats?.stats?.mastery_pct ?? null;

  return (
    <div style={{ minHeight: '100vh', background: HT.yellowBg, fontFamily: FONT_BODY, color: HT.text }}>
      <TopNavBar />
      <main style={{ padding: '18px 16px 120px', maxWidth: 960, margin: '0 auto', width: '100%' }}>

        {/* ── Title + main tabs ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 24, flex: '1 1 auto' }}>
            📖 Sổ từ
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[{ k: 'vocab', l: '📖 Từ vựng' }, { k: 'grammar', l: '📝 Ngữ pháp' }].map(x => (
              <button key={x.k} onClick={() => setParam('tab', x.k === 'vocab' ? '' : x.k)}
                style={pill(tab === x.k, HT.coralDark)}>{x.l}</button>
            ))}
          </div>
        </div>

        {tab === 'vocab' && (
          <>
            {/* ── Compact filter bar: search + 3 dropdowns + active chips ── */}
            <input
              value={q}
              onChange={e => setParam('q', e.target.value)}
              placeholder="🔍 Tìm từ (tiếng Anh hoặc nghĩa Việt)…"
              style={{
                width: '100%', padding: '11px 16px', marginBottom: 8, boxSizing: 'border-box',
                borderRadius: 14, border: `1px solid ${HT.border}`, outline: 'none',
                fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600, boxShadow: SHADOW_SM,
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              {[
                { k: 'level', icon: '🌟', label: 'Cấp độ', active: levelsSel.length,
                  summary: levelsSel.length ? levelsSel.map(l => l[0].toUpperCase() + l.slice(1)).join(', ') : 'Tất cả' },
                { k: 'topic', icon: '📚', label: 'Chủ đề', active: topicId ? 1 : 0,
                  summary: topicId ? (activeTopic?.name || '1 chủ đề') : 'Tất cả' },
                { k: 'status', icon: '📊', label: 'Trạng thái', active: statusSel.length,
                  summary: statusSel.length ? statusSel.map(k => STATUS_TABS.find(t => t.key === k)?.label || k).join(', ') : 'Tất cả' },
              ].map(f => (
                <button key={f.k} onClick={() => { setSheet(f.k); setTopicQuery(''); }} style={{
                  flex: 1, minWidth: 0, textAlign: 'left', padding: '8px 12px',
                  borderRadius: 12, cursor: 'pointer',
                  border: `2px solid ${f.active ? HT.teal : HT.border}`,
                  background: HT.white, boxShadow: SHADOW_SM, fontFamily: FONT_BODY,
                }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: HT.textMuted, letterSpacing: 0.5 }}>
                    {f.icon} {f.label.toUpperCase()}
                  </div>
                  <div style={{
                    fontSize: 12, fontWeight: 700, color: f.active ? HT.teal : HT.text,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{f.summary} ▾</div>
                </button>
              ))}
            </div>
            {(levelsSel.length > 0 || topicId || statusSel.length > 0) && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
                {levelsSel.map(l => (
                  <button key={l} onClick={() => setParam('level', levelsSel.filter(x => x !== l).join(','))}
                    style={{ ...pill(true), padding: '5px 10px', fontSize: 11 }}>
                    {LEVELS.find(x => x.key === l)?.icon} {LEVELS.find(x => x.key === l)?.label} ✕
                  </button>
                ))}
                {topicId && (
                  <button onClick={() => setParam('topic', '')}
                    style={{ ...pill(true, HT.greenDark), padding: '5px 10px', fontSize: 11 }}>
                    📚 {activeTopic?.name || 'Chủ đề'} ✕
                  </button>
                )}
                {statusSel.map(k => (
                  <button key={k} onClick={() => setParam('status', statusSel.filter(x => x !== k).join(','))}
                    style={{ ...pill(true, HT.yellowShadow), padding: '5px 10px', fontSize: 11 }}>
                    {STATUS_TABS.find(t => t.key === k)?.emoji} {STATUS_TABS.find(t => t.key === k)?.label} ✕
                  </button>
                ))}
                <button onClick={() => { setParam('level', ''); setParam('topic', ''); setParam('status', ''); }}
                  style={{ border: 'none', background: 'transparent', color: HT.textMuted, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: FONT_BODY }}>
                  Xóa hết
                </button>
              </div>
            )}
            <div style={{ fontSize: 12, fontWeight: 700, color: HT.textMuted, marginBottom: 8 }}>
              🔢 {visible.length} từ · 🟡 {counts.learning} đang học · 🟢 {counts.mastered} thuộc · ⚪ {counts.unexplored} chưa học
            </div>

            {/* Topic header card */}
            {topicId && activeTopic && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                background: HT.white, border: `2px solid ${HT.yellow}`, borderRadius: 16,
                padding: '12px 16px', marginBottom: 12, boxShadow: SHADOW_MD,
              }}>
                <img
                  src={`${CATEGORY_IMG_BASE}${activeTopic.code}.webp`}
                  alt=""
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  style={{ width: 58, height: 58, objectFit: 'contain', borderRadius: 12, background: HT.yellowBg }}
                />
                <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                  <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 18 }}>
                    {activeTopic.name}
                    {activeTopic.name_vi && <span style={{ fontSize: 13, color: HT.textSoft, marginLeft: 8 }}>{activeTopic.name_vi}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: HT.textMuted, fontWeight: 700 }}>
                    {topicFiltered ? `${topicFiltered.length} từ` : '…'}
                    {masteryPct != null && <> · 🟢 thuộc {masteryPct}%</>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={() => navigate(`/topics/${topicId}/play`)} style={{
                    padding: '10px 16px', border: 'none', borderRadius: 999,
                    background: `linear-gradient(135deg, ${HT.coral}, ${HT.coralDark})`,
                    color: HT.white, fontFamily: FONT_DISPLAY, fontSize: 13, fontWeight: 700,
                    boxShadow: `0 3px 0 ${HT.coralPress}`, cursor: 'pointer',
                  }}>▶ Học chủ đề này</button>
                  <button onClick={() => setGameModal(true)} style={{
                    padding: '10px 16px', borderRadius: 999,
                    border: `2px solid ${HT.teal}`, background: HT.white,
                    color: HT.teal, fontFamily: FONT_DISPLAY, fontSize: 13, fontWeight: 700,
                    cursor: 'pointer',
                  }}>🎮 Chọn game</button>
                </div>
              </div>
            )}

            {/* Practice group button */}
            {words && visible.length > 0 && (statusSel.length > 0 || topicId) && (
              <button onClick={() => practice(tabPracticePool)} style={{
                width: '100%', marginBottom: 12, padding: '13px 18px',
                border: 'none', borderRadius: 14,
                background: `linear-gradient(135deg, ${HT.green}, ${HT.greenDark})`,
                color: HT.white, fontFamily: FONT_DISPLAY, fontSize: 15, fontWeight: 700,
                boxShadow: `0 4px 0 ${HT.greenDark}`, cursor: 'pointer',
              }}>
                ▶ Luyện {Math.min(visible.length, MAX_SELECT)} từ nhóm này
                {visible.length > MAX_SELECT ? ` (trong ${visible.length})` : ''}
              </button>
            )}

            {/* ④ Word list */}
            {error && (
              <div style={{ padding: 18, background: HT.pinkBg, border: `1px solid ${HT.pink}`, borderRadius: 12, fontWeight: 700, color: HT.coralDark }}>
                Không tải được danh sách từ ({error}). Thử tải lại trang nhé.
              </div>
            )}
            {!words && !error && (
              <div style={{ textAlign: 'center', padding: 50, color: HT.textSoft, fontWeight: 700 }}>🦉 Đang tải sổ từ…</div>
            )}
            {words && visible.length === 0 && (
              <div style={{
                padding: '48px 20px', textAlign: 'center', background: HT.white,
                border: `2px dashed ${HT.border}`, borderRadius: 16, color: HT.textMuted, fontWeight: 700,
              }}>
                {q ? 'Không tìm thấy từ nào khớp.' : 'Chưa có từ nào trong nhóm này.'}
              </div>
            )}
            {words && visible.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {visible.map(w => {
                  const info = STATUS_INFO[w.status] || STATUS_INFO.unexplored;
                  const isSel = selected.has(w.tag_id);
                  return (
                    <div key={w.tag_id} onClick={() => toggleSelect(w.tag_id)} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      background: isSel ? HT.greenBg : HT.white,
                      border: `2px solid ${isSel ? HT.green : HT.border}`,
                      borderRadius: 14, padding: '10px 12px', boxShadow: SHADOW_SM,
                      cursor: 'pointer', transition: 'all 0.12s',
                    }}>
                      <span style={{
                        width: 22, height: 22, borderRadius: 7, flexShrink: 0,
                        border: `2px solid ${isSel ? HT.greenDark : HT.border}`,
                        background: isSel ? HT.green : HT.white,
                        color: HT.white, fontSize: 14, fontWeight: 900,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>{isSel ? '✓' : ''}</span>
                      <button onClick={(e) => { e.stopPropagation(); speak(w); }} aria-label={`Phát âm ${w.name}`} style={{
                        width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                        border: `1px solid ${HT.border}`, background: HT.yellowBg,
                        fontSize: 15, cursor: 'pointer',
                      }}>🔊</button>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 16, lineHeight: 1.15 }}>
                          {w.name}
                        </div>
                        {w.name_vi && (
                          <div style={{ fontSize: 12, color: HT.textSoft, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {w.name_vi}
                          </div>
                        )}
                      </div>
                      {levelsSel.length !== 1 && w.level && (
                        <span style={{
                          flexShrink: 0, padding: '3px 8px', borderRadius: 999, fontSize: 10,
                          fontWeight: 800, background: HT.yellowBg, color: HT.brownDark,
                          border: `1px solid ${HT.border}`,
                        }}>{w.level}</span>
                      )}
                      <span title={`Cấp ${w.current_tier}/5`} style={{ fontSize: 11, letterSpacing: 1, flexShrink: 0, color: HT.yellowShadow }}>
                        {tierStars(w.current_tier)}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setStatusWord(w); }}
                        title="Đổi trạng thái"
                        style={{
                          flexShrink: 0, padding: '4px 10px', borderRadius: 999,
                          background: info.bg, color: info.color, fontSize: 11, fontWeight: 800,
                          whiteSpace: 'nowrap', border: `1px solid ${info.color}33`,
                          cursor: 'pointer', fontFamily: FONT_BODY,
                        }}>{info.emoji} {info.label} ▾</button>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── GRAMMAR tab ── */}
        {tab === 'grammar' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {!topics && <div style={{ textAlign: 'center', padding: 50, color: HT.textSoft, fontWeight: 700 }}>🦉 Đang tải…</div>}
            {grammarTopics.map(t => {
              const open = grammarOpen === t.tag_id;
              const leaves = grammarLeaves[t.tag_id];
              return (
                <div key={t.tag_id} style={{
                  background: HT.white, border: `2px solid ${open ? HT.yellow : HT.border}`,
                  borderRadius: 16, boxShadow: SHADOW_SM, overflow: 'hidden',
                }}>
                  <div onClick={() => openGrammar(t.tag_id)} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', cursor: 'pointer',
                  }}>
                    <span style={{ fontSize: 26 }}>📝</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 16 }}>
                        {t.name}
                        {t.name_vi && <span style={{ fontSize: 12, color: HT.textSoft, marginLeft: 8 }}>{t.name_vi}</span>}
                      </div>
                      <div style={{ fontSize: 11, color: HT.textMuted, fontWeight: 700 }}>{t.child_count} quy tắc</div>
                    </div>
                    <span style={{
                      flexShrink: 0, padding: '9px 16px', borderRadius: 999,
                      background: open ? HT.yellow : `linear-gradient(135deg, ${HT.coral}, ${HT.coralDark})`,
                      color: open ? HT.text : HT.white, fontFamily: FONT_DISPLAY, fontSize: 13, fontWeight: 700,
                    }}>{open ? '📖 Đang xem' : '▶ Bắt đầu'}</span>
                    <span style={{ color: HT.textMuted, fontSize: 13 }}>{open ? '▲' : '▼'}</span>
                  </div>
                  {open && (
                    <div style={{ borderTop: `1px dashed ${HT.border}`, padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {!leaves && <div style={{ color: HT.textSoft, fontWeight: 700, fontSize: 13 }}>Đang tải…</div>}
                      {leaves && leaves.map(l => {
                        const info = STATUS_INFO[l.status] || STATUS_INFO.unexplored;
                        const dOpen = grammarLeaf === l.tag_id;
                        const lines = (s) => String(s || '').split('\n').map(x => x.trim()).filter(Boolean);
                        const formula = typeof l.formula === 'string' ? l.formula : (l.formula?.en || l.formula?.vi || '');
                        const usageVi = lines(l.usage?.vi), usageEn = lines(l.usage?.en);
                        const exVi = lines(l.example?.vi), exEn = lines(l.example?.en);
                        const hasDetail = formula || usageVi.length || usageEn.length || exVi.length || exEn.length;
                        return (
                          <div key={l.tag_id} style={{ border: `1px solid ${HT.border}`, borderRadius: 12, overflow: 'hidden' }}>
                            <div onClick={() => setGrammarLeaf(dOpen ? null : l.tag_id)} style={{
                              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', cursor: 'pointer',
                              background: dOpen ? HT.yellowBg : HT.white,
                            }}>
                              <span style={{ fontSize: 14 }}>✏️</span>
                              <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700 }}>
                                {l.name}
                                {l.name_vi && <span style={{ fontSize: 11, color: HT.textSoft, marginLeft: 6, fontWeight: 600 }}>{l.name_vi}</span>}
                              </div>
                              <span style={{
                                flexShrink: 0, padding: '3px 9px', borderRadius: 999,
                                background: info.bg, color: info.color, fontSize: 10, fontWeight: 800,
                              }}>{info.emoji} {info.label}</span>
                              <span style={{ color: HT.textMuted, fontSize: 12 }}>{dOpen ? '▲' : '▼'}</span>
                            </div>
                            {dOpen && (
                              <div style={{ borderTop: `1px dashed ${HT.border}`, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12, background: HT.white }}>
                                {formula && (
                                  <div style={{
                                    background: HT.yellowBg, border: `1px solid ${HT.yellowShadow}`, borderRadius: 10,
                                    padding: '10px 12px', fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 15,
                                    textAlign: 'center', color: HT.coralDark,
                                  }}>{formula}</div>
                                )}
                                {(usageVi.length > 0 || usageEn.length > 0) && (
                                  <div>
                                    <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 13, color: HT.coralDark, marginBottom: 5 }}>📌 Cách dùng</div>
                                    {usageVi.map((x, i) => <div key={'uv' + i} style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.5, marginBottom: 2 }}>• {x}</div>)}
                                    {usageEn.map((x, i) => <div key={'ue' + i} style={{ fontSize: 12.5, color: HT.textSoft, fontStyle: 'italic', lineHeight: 1.5 }}>{x}</div>)}
                                  </div>
                                )}
                                {(exVi.length > 0 || exEn.length > 0) && (
                                  <div>
                                    <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 13, color: HT.greenDark, marginBottom: 5 }}>💬 Ví dụ</div>
                                    {exEn.map((x, i) => <div key={'xe' + i} style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.5, color: HT.text }}>“{x}”</div>)}
                                    {exVi.map((x, i) => <div key={'xv' + i} style={{ fontSize: 12.5, color: HT.textSoft, lineHeight: 1.5, marginBottom: 2 }}>{x}</div>)}
                                  </div>
                                )}
                                {!hasDetail && <div style={{ fontSize: 13, color: HT.textSoft }}>Chưa có nội dung chi tiết cho quy tắc này.</div>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* ── Floating practice bar (manual selection) ── */}
      {tab === 'vocab' && selected.size > 0 && (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 50,
          padding: '12px 16px calc(12px + env(safe-area-inset-bottom))',
          background: 'rgba(255,248,231,0.96)', borderTop: `1px solid ${HT.border}`,
          backdropFilter: 'blur(6px)',
          display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center',
        }}>
          <button onClick={() => setSelected(new Set())} style={{
            padding: '11px 16px', borderRadius: 999, border: `1px solid ${HT.border}`,
            background: HT.white, color: HT.textSoft, fontFamily: FONT_BODY,
            fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}>Bỏ chọn</button>
          <button onClick={() => practice([...selected])} style={{
            padding: '13px 26px', border: 'none', borderRadius: 999,
            background: `linear-gradient(135deg, ${HT.coral}, ${HT.coralDark})`,
            color: HT.white, fontFamily: FONT_DISPLAY, fontSize: 15, fontWeight: 700,
            boxShadow: `0 4px 0 ${HT.coralPress}`, cursor: 'pointer',
          }}>▶ Luyện {selected.size} từ đã chọn{selected.size >= MAX_SELECT ? ' (tối đa)' : ''}</button>
        </div>
      )}

      {/* ── Floating "start practising unlearned words" bar (nothing selected) ── */}
      {tab === 'vocab' && selected.size === 0 && unlearnedPool.length > 0 && (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 50,
          padding: '12px 16px calc(12px + env(safe-area-inset-bottom))',
          background: 'rgba(255,248,231,0.96)', borderTop: `1px solid ${HT.border}`,
          backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <button onClick={() => practice(unlearnedPool)} style={{
            width: '100%', maxWidth: 420, padding: '15px 26px', border: 'none', borderRadius: 999,
            background: `linear-gradient(135deg, ${HT.coral}, ${HT.coralDark})`,
            color: HT.white, fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 700,
            boxShadow: `0 4px 0 ${HT.coralPress}`, cursor: 'pointer',
          }}>▶ Bắt đầu luyện tập các từ chưa thuộc</button>
        </div>
      )}

      {/* ── Word status sheet ── */}
      {statusWord && (
        <div onClick={() => setStatusWord(null)} style={{
          position: 'fixed', inset: 0, zIndex: 110, background: 'rgba(45,22,60,0.45)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: '100%', maxWidth: 560, background: HT.yellowBg,
            borderRadius: '20px 20px 0 0', padding: '16px 16px 28px',
          }}>
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 17, marginBottom: 4 }}>
              {statusWord.name}
              {statusWord.name_vi && <span style={{ fontSize: 13, color: HT.textSoft, marginLeft: 8 }}>{statusWord.name_vi}</span>}
            </div>
            <div style={{ fontSize: 12, color: HT.textMuted, fontWeight: 700, marginBottom: 12 }}>
              Chuyển trạng thái từ này sang:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { k: 'mastered', info: STATUS_INFO.mastered, note: 'Bé đã biết từ này — tính là thuộc ở MỌI cấp độ, sẽ chỉ ôn lại định kỳ' },
                { k: 'in_progress', info: STATUS_INFO.in_progress, note: 'Đánh dấu đang học (giữ tiến độ hiện có)' },
                { k: 'unexplored', info: STATUS_INFO.unexplored, note: 'Xóa toàn bộ tiến độ — học lại từ đầu' },
              ].filter(o => {
                const cur = statusWord.status === 'needs_improvement' || statusWord.status === 'review_due' ? 'in_progress' : statusWord.status;
                return o.k !== cur;
              }).map(o => (
                <button key={o.k} onClick={() => changeWordStatus(statusWord, o.k)} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px',
                  borderRadius: 14, cursor: 'pointer', fontFamily: FONT_BODY, textAlign: 'left',
                  border: `2px solid ${o.info.color}55`, background: HT.white,
                }}>
                  <span style={{ fontSize: 20 }}>{o.info.emoji}</span>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: o.info.color }}>{o.info.label}</div>
                    <div style={{ fontSize: 11, color: HT.textSoft, fontWeight: 600 }}>{o.note}</div>
                  </div>
                </button>
              ))}
              <button onClick={() => setStatusWord(null)} style={{
                padding: '11px', borderRadius: 14, border: `1px solid ${HT.border}`,
                background: HT.white, color: HT.textSoft, fontWeight: 700,
                fontFamily: FONT_BODY, cursor: 'pointer',
              }}>Hủy</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Filter bottom-sheet (level / topic / status) ── */}
      {sheet && (
        <div onClick={() => setSheet(null)} style={{
          position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(45,22,60,0.45)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: '100%', maxWidth: 560, maxHeight: '72vh', overflowY: 'auto',
            background: HT.yellowBg, borderRadius: '20px 20px 0 0', padding: '16px 16px 28px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 17, flex: 1 }}>
                {sheet === 'level' && '🌟 Chọn cấp độ (chọn nhiều được)'}
                {sheet === 'topic' && '📚 Chọn chủ đề'}
                {sheet === 'status' && '📊 Trạng thái (chọn nhiều được)'}
              </div>
              <button onClick={() => setSheet(null)} style={{
                padding: '8px 18px', borderRadius: 999, border: 'none',
                background: HT.coralDark, color: HT.white, fontWeight: 800,
                fontFamily: FONT_DISPLAY, cursor: 'pointer',
              }}>Xong</button>
            </div>

            {sheet === 'level' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {LEVELS.filter(l => l.key !== 'all').map(lv => {
                  const on = levelsSel.includes(lv.key);
                  return (
                    <button key={lv.key}
                      onClick={() => setParam('level', (on ? levelsSel.filter(x => x !== lv.key) : [...levelsSel, lv.key]).join(','))}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                        borderRadius: 14, cursor: 'pointer', fontFamily: FONT_BODY,
                        border: `2px solid ${on ? HT.teal : HT.border}`,
                        background: on ? '#E5F7F5' : HT.white, textAlign: 'left',
                      }}>
                      <span style={{
                        width: 22, height: 22, borderRadius: 7, flexShrink: 0,
                        border: `2px solid ${on ? HT.teal : HT.border}`,
                        background: on ? HT.teal : HT.white, color: HT.white,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, fontWeight: 900,
                      }}>{on ? '✓' : ''}</span>
                      <span style={{ fontSize: 15, fontWeight: 700 }}>{lv.icon} {lv.label}</span>
                    </button>
                  );
                })}
                <div style={{ fontSize: 11, color: HT.textMuted, fontWeight: 600 }}>Không chọn gì = tất cả cấp độ</div>
              </div>
            )}

            {sheet === 'status' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {STATUS_TABS.filter(t => t.key !== 'all').map(t => {
                  const on = statusSel.includes(t.key);
                  return (
                    <button key={t.key}
                      onClick={() => setParam('status', (on ? statusSel.filter(x => x !== t.key) : [...statusSel, t.key]).join(','))}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                        borderRadius: 14, cursor: 'pointer', fontFamily: FONT_BODY,
                        border: `2px solid ${on ? HT.teal : HT.border}`,
                        background: on ? '#E5F7F5' : HT.white, textAlign: 'left',
                      }}>
                      <span style={{
                        width: 22, height: 22, borderRadius: 7, flexShrink: 0,
                        border: `2px solid ${on ? HT.teal : HT.border}`,
                        background: on ? HT.teal : HT.white, color: HT.white,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, fontWeight: 900,
                      }}>{on ? '✓' : ''}</span>
                      <span style={{ fontSize: 15, fontWeight: 700 }}>{t.emoji} {t.label}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 800, color: HT.textMuted }}>{counts[t.key] ?? 0}</span>
                    </button>
                  );
                })}
                <div style={{ fontSize: 11, color: HT.textMuted, fontWeight: 600 }}>Không chọn gì = mọi trạng thái</div>
              </div>
            )}

            {sheet === 'topic' && (
              <>
                <input
                  autoFocus
                  value={topicQuery}
                  onChange={e => setTopicQuery(e.target.value)}
                  placeholder="🔍 Tìm chủ đề…"
                  style={{
                    width: '100%', padding: '11px 14px', marginBottom: 10, boxSizing: 'border-box',
                    borderRadius: 12, border: `1px solid ${HT.border}`, outline: 'none',
                    fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600,
                  }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <button onClick={() => { setParam('topic', ''); setSheet(null); }} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px',
                    borderRadius: 12, cursor: 'pointer', fontFamily: FONT_BODY, textAlign: 'left',
                    border: `2px solid ${!topicId ? HT.greenDark : HT.border}`,
                    background: !topicId ? HT.greenBg : HT.white,
                  }}>
                    <span style={{ fontSize: 16 }}>📚</span>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>Tất cả chủ đề</span>
                  </button>
                  {vocabTopics
                    .filter(t => {
                      const tq = topicQuery.trim().toLowerCase();
                      if (!tq) return true;
                      return (t.name || '').toLowerCase().includes(tq) || (t.name_vi || '').toLowerCase().includes(tq);
                    })
                    .map(t => (
                      <button key={t.tag_id} onClick={() => { setParam('topic', t.tag_id); setSheet(null); }} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px',
                        borderRadius: 12, cursor: 'pointer', fontFamily: FONT_BODY, textAlign: 'left',
                        border: `2px solid ${topicId === t.tag_id ? HT.greenDark : HT.border}`,
                        background: topicId === t.tag_id ? HT.greenBg : HT.white,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 700 }}>{t.name}</div>
                          {t.name_vi && <div style={{ fontSize: 11, color: HT.textSoft, fontWeight: 600 }}>{t.name_vi}</div>}
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 800, color: HT.textMuted, flexShrink: 0 }}>{t.child_count} từ</span>
                      </button>
                    ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Game picker modal ── */}
      {gameModal && topicId && (
        <div onClick={() => setGameModal(false)} style={{
          position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(45,22,60,0.45)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: '100%', maxWidth: 560, maxHeight: '78vh', overflowY: 'auto',
            background: HT.yellowBg, borderRadius: '20px 20px 0 0', padding: '16px 16px 28px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 17, flex: 1 }}>
                🎮 Chọn game — {activeTopic?.name}
              </div>
              <button onClick={() => setGameModal(false)} style={{
                width: 32, height: 32, borderRadius: '50%', border: `1px solid ${HT.border}`,
                background: HT.white, cursor: 'pointer', fontWeight: 700,
              }}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
              {VOCAB_GAMES_CATALOG.map(g => (
                <button key={g.code}
                  onClick={() => navigate(`/library/topics/${topicId}/game/${g.code}`)}
                  style={{
                    textAlign: 'left', padding: 12, borderRadius: 14,
                    border: `2px solid ${HT.border}`, background: HT.white,
                    cursor: 'pointer', fontFamily: FONT_BODY,
                  }}>
                  <div style={{ fontSize: 24 }}>{g.icon}</div>
                  <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 14, marginTop: 4 }}>
                    {g.name_vi}
                    <span style={{
                      marginLeft: 6, fontSize: 10, fontWeight: 800, color: HT.brownDark,
                      background: HT.yellowSoft, borderRadius: 999, padding: '1px 7px',
                    }}>T{g.tier}</span>
                  </div>
                  <div style={{ fontSize: 11, color: HT.textSoft, fontWeight: 600, marginTop: 2 }}>{g.desc}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
